/**
 * SongTag — the compact song-info card pinned to the top of an editor's right
 * sidebar (cover + author + song name + duration). Same 38px height as the left
 * header's editor pill so both sidebars' first row lines up.
 *
 * Placement (fixed wrapper, bottom margin) is owned by the caller; this is just
 * the card itself so it looks identical across all editors.
 */

import React, { useState, useEffect } from 'react';
import { color } from './theme';

export default function SongTag({
  coverUrl, primary, secondary, duration, onClick, round = false, bare = false, spinning = false,
}: {
  coverUrl: string | null;
  /** Bold first line (the editors headline the artist here). */
  primary: string;
  /** Second line (the song / track name). */
  secondary: string;
  duration: number;
  onClick?: () => void;
  /** Fully-rounded (pill) corners — used in the bottom playback bar. */
  round?: boolean;
  /** No own chrome (transparent, borderless, fills its parent) — used when the
   *  caller wraps it in its own pill so it matches the surrounding pills. In this
   *  mode the cover is a round "vinyl" disc the size of the play button. */
  bare?: boolean;
  /** Spin the round cover like a vinyl record (while the track is playing). */
  spinning?: boolean;
}) {
  const mm = Math.floor(duration / 60);
  const ss = Math.floor(duration % 60).toString().padStart(2, '0');
  const fmt = Number.isFinite(duration) && duration > 0 ? `${mm}:${ss}` : null;

  // Try the artwork URL optimistically and fall back to the 🎵 placeholder if it
  // 404s. The backend reports has_artwork=false for not-yet-hydrated maps even
  // when the .synth has art, so callers can't reliably pre-gate on it.
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [coverUrl]);
  const showCover = !!coverUrl && !failed;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...cardStyle,
        ...(round ? { borderRadius: 999 } : null),
        ...(bare ? { background: 'transparent', border: 'none', borderRadius: 0, height: '100%', padding: 0 } : null),
      }}
      title="Edit map info"
    >
      <div
        className={bare && spinning ? 'vinyl-spin' : undefined}
        style={{
          ...(bare ? vinylStyle : thumbStyle),
          backgroundColor: showCover ? 'transparent' : 'rgba(20, 12, 40, 0.7)',
          overflow: 'hidden',
        }}
      >
        {showCover && (
          <img
            src={coverUrl!}
            alt=""
            onError={() => setFailed(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {!showCover && <span style={{ fontSize: 16, opacity: 0.55 }}>🎵</span>}
        {/* Vinyl centre hole — sells the spinning-disc look. */}
        {bare && <span style={vinylHoleStyle} />}
      </div>
      <div style={textStyle}>
        <div style={titleStyle}>{primary}</div>
        <div style={subStyle}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{secondary}</span>
          {fmt && <span style={{ color: color.inkFaint }}>·</span>}
          {fmt && <span style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{fmt}</span>}
        </div>
      </div>
    </button>
  );
}

const cardStyle: React.CSSProperties = {
  height: 38,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '0 6px',
  background: 'oklch(0.17 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.55)',
  border: '1px solid var(--line-accent)',
  borderRadius: 10,
  cursor: 'pointer',
  color: 'inherit',
  fontFamily: 'inherit',
  textAlign: 'left',
  transition: 'border-color 0.15s ease',
};

const thumbStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 5,
  flexShrink: 0,
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: '1px solid var(--line-accent)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

// Round "vinyl" cover used in the bottom playback bar — same size as the play
// button (36) so it nestles into the pill's left curve.
const vinylStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: '1px solid var(--line-accent)',
  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.45), inset 0 0 8px rgba(0,0,0,0.5)',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const vinylHoleStyle: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: 'oklch(0.12 calc(0.02 * var(--tint-c)) var(--tint-h))',
  border: '1px solid rgba(255,255,255,0.3)',
};

const textStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: 1,
};

const titleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: color.ink,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  letterSpacing: 0.3,
  lineHeight: 1.2,
};

const subStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 10,
  color: color.inkDim,
  letterSpacing: 0.5,
  marginTop: 2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};
